import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Camera, Wine, Sparkles, BarChart3 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { openExternal } from '@/utils/openExternal';

/* ── Nav ── */
function LandingNav() {
  const navigate = useNavigate();
  return (
    <nav
      aria-label="Main navigation"
      className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 sm:px-10 py-4 bg-[var(--rc-surface-primary)]/88 backdrop-blur-md border-b border-[var(--rc-border-subtle)]"
    >
      <a href="/" className="font-[family-name:var(--rc-font-display)] text-[28px] tracking-[0.08em] text-[var(--rc-ink-primary)] no-underline font-bold">
        Rave <span className="text-[var(--rc-accent-pink)]">Cave</span>
      </a>
      <div className="flex items-center gap-6">
        <button
          onClick={() => navigate({ to: '/login' })}
          className="font-[family-name:var(--rc-font-mono)] text-[11px] tracking-[0.1em] uppercase text-[var(--rc-surface-primary)] bg-[var(--rc-ink-primary)] px-5 py-2.5 rounded-[var(--rc-button-radius)] cursor-pointer border-none hover:bg-[var(--rc-accent-pink)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--rc-accent-pink)]"
        >
          Sign in
        </button>
      </div>
    </nav>
  );
}

/* ── Hero ── */
function Hero() {
  const navigate = useNavigate();
  return (
    <section className="min-h-dvh flex flex-col justify-center pt-24 pb-16 px-6 sm:px-10 relative">
      <div className="font-[family-name:var(--rc-font-mono)] text-[11px] tracking-[0.15em] uppercase text-[var(--rc-ink-tertiary)] mb-4">
        <span className="text-[var(--rc-accent-acid)]">●</span> Now in beta — limited access
      </div>
      <h1 className="font-[family-name:var(--rc-font-display)] text-[clamp(56px,10vw,140px)] leading-[0.9] tracking-[0.02em] text-[var(--rc-ink-primary)] max-w-[900px] font-black">
        Know what you have.
        <span className="text-[var(--rc-accent-pink)] block">Drink what</span>
        you love.
      </h1>
      <div className="mt-12 max-w-[480px]">
        <p className="text-lg sm:text-xl leading-relaxed text-[var(--rc-ink-secondary)] font-[family-name:var(--rc-font-body)]">
          Know what you have. Drink what you love.
        </p>
        <div className="flex flex-col items-start gap-3 mt-8">
          <button
            onClick={() => navigate({ to: '/login' })}
            className="font-[family-name:var(--rc-font-mono)] text-[12px] tracking-[0.1em] uppercase text-[var(--rc-ink-primary)] bg-[var(--rc-accent-acid)] px-8 py-4 rounded-[var(--rc-button-radius)] cursor-pointer border-none hover:-translate-y-0.5 transition-transform focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--rc-accent-acid)]"
          >
            Get started
          </button>
          <span className="font-[family-name:var(--rc-font-mono)] text-[10px] tracking-[0.08em] uppercase text-[var(--rc-ink-tertiary)]">
            2 months free · No card required
          </span>
        </div>
      </div>
    </section>
  );
}

/* ── Marquee ── */
function Marquee() {
  const items = [
    'Camera-first registration',
    'AI-powered cellar intelligence',
    'Remy knows your cellar',
    'Drink window tracking',
    'Voice-enabled sommelier',
    'Works on any device',
  ];
  const track = [...items, ...items]; // duplicate for seamless loop
  return (
    <div className="overflow-hidden py-5 bg-[var(--rc-ink-primary)]" aria-hidden="true">
      <div className="flex whitespace-nowrap motion-safe:animate-[marquee_24s_linear_infinite]">
        {track.map((text, i) => (
          <span key={i} className="flex items-center gap-10 px-5">
            <span className={`font-[family-name:var(--rc-font-mono)] text-[11px] tracking-[0.15em] uppercase shrink-0 ${text === 'Remy knows your cellar' ? 'text-[var(--rc-accent-acid)]' : 'text-[var(--rc-ink-ghost)]'}`}>
              {text}
            </span>
            <span className="text-[var(--rc-accent-pink)] text-[11px]">·</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Feature Card ── */
function FeatureCard({ num, title, body, icon: Icon }: { num: string; title: string; body: string; icon: LucideIcon }) {
  return (
    <div className="group bg-[var(--rc-surface-primary)] p-10 sm:p-12 relative overflow-hidden hover:bg-[var(--rc-surface-elevated)] transition-colors">
      <div className="flex items-start justify-between mb-6">
        <span aria-hidden="true" className="font-[family-name:var(--rc-font-display)] text-[56px] leading-none text-[var(--rc-border-subtle)] group-hover:text-[var(--rc-accent-pink)] transition-colors font-bold">{num}</span>
        <Icon size={28} className="text-[var(--rc-ink-ghost)] group-hover:text-[var(--rc-accent-pink)] transition-colors mt-2" strokeWidth={1.5} />
      </div>
      <h3 className="font-[family-name:var(--rc-font-display)] text-[24px] sm:text-[28px] tracking-[0.05em] text-[var(--rc-ink-primary)] mb-4 leading-none font-bold">{title}</h3>
      <p className="text-[15px] text-[var(--rc-ink-tertiary)] leading-relaxed font-[family-name:var(--rc-font-body)]">{body}</p>
      <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-[var(--rc-accent-pink)] group-hover:w-full transition-all duration-300" />
    </div>
  );
}

/* ── Features ── */
function Features() {
  return (
    <section id="features" className="py-20 sm:py-24 px-6 sm:px-10 relative z-[1]">
      <h2 className="font-[family-name:var(--rc-font-mono)] text-[10px] tracking-[0.2em] uppercase text-[var(--rc-ink-tertiary)] mb-14 flex items-center gap-4 after:content-[''] after:flex-1 after:h-px after:bg-[var(--rc-border-subtle)]">What it does</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[var(--rc-border-subtle)] border border-[var(--rc-border-subtle)]">
        <FeatureCard num="01" title="Scan & Register" body="Point your camera at any label. Remy reads the producer, vintage, region, and varietal — and files it away before you've finished pouring." icon={Camera} />
        <FeatureCard num="02" title="Drink Windows" body="Know what's peaking now, what needs more time, and what you've already missed. Your cellar stays honest with you." icon={Wine} />
        <FeatureCard num="03" title="Smart Recommendations" body="Tonight's dinner. A gift for someone difficult. A bottle to impress. Remy knows your cellar and makes the call." icon={Sparkles} />
        <FeatureCard num="04" title="Pulse Dashboard" body="Collection insights, data stories, and drink-now prompts surfaced before you have to ask. Your cellar tells you what it needs." icon={BarChart3} />
      </div>
    </section>
  );
}

/* ── App Preview ── */
const APP_SCREENSHOTS = [
  { src: '/screenshots/cellar.png', alt: 'Rave Cave cellar view showing wine cards with label photos, maturity chips, and vintage details', label: 'Your cellar' },
  { src: '/screenshots/scan.png', alt: 'Rave Cave scan flow showing a wine label being captured and automatically extracted', label: 'Scan a label' },
  { src: '/screenshots/remy.png', alt: 'Remy AI sommelier conversation recommending wines for a dinner occasion', label: 'Ask Remy' },
];

function AppPreview() {
  return (
    <section className="px-6 sm:px-10 pb-20">
      <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
        {APP_SCREENSHOTS.map((shot) => (
          <div key={shot.label} className="flex flex-col items-center gap-3">
            <div className="w-[240px] sm:w-[260px] rounded-[16px] overflow-hidden border border-[var(--rc-border-subtle)] shadow-lg bg-[var(--rc-surface-elevated)]">
              <img
                src={shot.src}
                alt={shot.alt}
                className="w-full h-auto"
                loading="lazy"
                decoding="async"
              />
            </div>
            <span className="font-[family-name:var(--rc-font-mono)] text-[10px] tracking-[0.15em] uppercase text-[var(--rc-ink-tertiary)]">{shot.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── Remy Section ── */
function RemySection() {
  const navigate = useNavigate();
  return (
    <section id="remy" className="py-20 sm:py-24 px-6 sm:px-10 bg-[var(--rc-ink-primary)] relative z-[1] overflow-hidden">
      <div className="absolute right-[-20px] top-1/2 -translate-y-1/2 font-[family-name:var(--rc-font-display)] text-[200px] sm:text-[300px] leading-none text-white/[0.03] pointer-events-none tracking-[0.02em] whitespace-nowrap font-bold select-none" aria-hidden="true">REMY</div>
      <div className="max-w-[800px] relative">
        <div className="font-[family-name:var(--rc-font-mono)] text-[10px] tracking-[0.2em] uppercase text-[var(--rc-accent-pink)] mb-8">Your AI sommelier</div>
        <h2 className="font-[family-name:var(--rc-font-display)] text-[clamp(40px,7vw,80px)] leading-[0.95] tracking-[0.02em] text-[var(--rc-surface-primary)] mb-10 font-black">
          Meet <span className="text-[var(--rc-accent-pink)]">Remy.</span><br />He's been waiting.
        </h2>
        <p className="text-lg sm:text-xl text-white/60 leading-relaxed max-w-[560px] mb-14 font-[family-name:var(--rc-font-body)]">
          Not a chatbot. Not a search bar. Remy is a sommelier who has read every wine book ever written — and who knows exactly what's in your cellar right now.
        </p>
        <div className="flex flex-col gap-4 max-w-[520px]">
          <div className="self-end max-w-[80%] px-5 py-4 rounded-[var(--rc-button-radius)] bg-white/[0.06] text-white/70 text-[15px] leading-relaxed border border-white/[0.08] font-[family-name:var(--rc-font-body)]">
            <div className="font-[family-name:var(--rc-font-mono)] text-[9px] tracking-[0.15em] uppercase mb-1.5 opacity-50">You</div>
            What's the difference between Barolo and Barbaresco? And do I have any?
          </div>
          <div className="px-5 py-4 rounded-[var(--rc-button-radius)] bg-[var(--rc-accent-pink)]/[0.12] text-[var(--rc-surface-primary)] text-[15px] leading-relaxed border-l-2 border-[var(--rc-accent-pink)] font-[family-name:var(--rc-font-body)]">
            <div className="font-[family-name:var(--rc-font-mono)] text-[9px] tracking-[0.15em] uppercase mb-1.5 opacity-50">Remy</div>
            Both are 100% Nebbiolo from Piedmont, but Barolo tends to be more structured and age-worthy while Barbaresco is a touch more elegant and approachable younger. You have a 2018 Gaja Barbaresco in your cellar — it's drinking beautifully right now. Want me to find a food pairing for it?
          </div>
        </div>
        <button
          onClick={() => navigate({ to: '/login' })}
          className="mt-10 font-[family-name:var(--rc-font-mono)] text-[11px] tracking-[0.1em] uppercase text-[var(--rc-ink-primary)] bg-[var(--rc-accent-acid)] px-6 py-3 rounded-[var(--rc-button-radius)] cursor-pointer border-none hover:-translate-y-0.5 transition-transform focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--rc-accent-acid)]"
        >
          Start a conversation with Remy
        </button>
      </div>
    </section>
  );
}

/* ── Social Proof ── */
function SocialProof() {
  const stats = [
    { value: '3', unit: 'sec', label: 'Average label scan time' },
    { value: '98', unit: '%', label: 'Extraction accuracy on clear labels' },
    { value: '5', unit: 'levels', label: 'Maturity tracking for every bottle' },
    { value: '24/7', unit: '', label: 'Remy never sleeps' },
  ];
  return (
    <section className="py-16 sm:py-20 px-6 sm:px-10 relative z-[1]">
      <h2 className="font-[family-name:var(--rc-font-mono)] text-[10px] tracking-[0.2em] uppercase text-[var(--rc-ink-tertiary)] mb-14 flex items-center gap-4 after:content-[''] after:flex-1 after:h-px after:bg-[var(--rc-border-subtle)]">What to expect</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
        {stats.map((s) => (
          <div key={s.label} className="border-t-2 border-[var(--rc-ink-primary)] pt-5">
            <div className="font-[family-name:var(--rc-font-display)] text-[48px] sm:text-[56px] leading-none tracking-[0.02em] text-[var(--rc-ink-primary)] mb-2 font-bold">
              {s.value}<span className="text-[var(--rc-accent-pink)]">{s.unit}</span>
            </div>
            <div className="font-[family-name:var(--rc-font-mono)] text-[11px] tracking-[0.1em] uppercase text-[var(--rc-ink-tertiary)]">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── Pricing ── */
function Pricing() {
  const navigate = useNavigate();
  return (
    <section id="pricing" className="py-20 sm:py-24 px-6 sm:px-10 bg-[var(--rc-surface-secondary)] relative z-[1]">
      <h2 className="font-[family-name:var(--rc-font-mono)] text-[10px] tracking-[0.2em] uppercase text-[var(--rc-ink-tertiary)] mb-14 flex items-center gap-4 after:content-[''] after:flex-1 after:h-px after:bg-[var(--rc-border-subtle)]">Pricing</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--rc-border-emphasis)] max-w-[860px] mx-auto border-2 border-[var(--rc-border-emphasis)]">
        {/* Free tier */}
        <div className="bg-[var(--rc-surface-primary)] p-10 sm:p-12">
          <div className="font-[family-name:var(--rc-font-display)] text-[36px] sm:text-[40px] tracking-[0.05em] leading-none mb-2 font-bold">Free</div>
          <div className="font-[family-name:var(--rc-font-display)] text-[64px] sm:text-[72px] leading-none tracking-[0.02em] text-[var(--rc-ink-primary)] mb-1 font-bold">$0</div>
          <div className="font-[family-name:var(--rc-font-mono)] text-[11px] tracking-[0.1em] uppercase text-[var(--rc-ink-tertiary)] mb-8">Always free</div>
          <ul className="flex flex-col gap-3 mb-10 list-none">
            {[
              'Up to 50 bottles in your cellar',
              'Camera-first label scanning',
              '5 Remy conversations per day',
              'Drink window tracking',
              'Works on any device',
            ].map(f => (
              <li key={f} className="flex items-start gap-2.5 text-[15px] text-[var(--rc-ink-secondary)] leading-snug font-[family-name:var(--rc-font-body)]">
                <span className="font-[family-name:var(--rc-font-mono)] text-[11px] text-[var(--rc-accent-pink)] shrink-0 mt-0.5">—</span>{f}
              </li>
            ))}
          </ul>
          <button
            onClick={() => navigate({ to: '/login' })}
            className="font-[family-name:var(--rc-font-mono)] text-[11px] tracking-[0.1em] uppercase px-7 py-3.5 rounded-[var(--rc-button-radius)] cursor-pointer bg-transparent text-[var(--rc-ink-primary)] border border-[var(--rc-ink-primary)] hover:bg-[var(--rc-ink-primary)] hover:text-[var(--rc-surface-primary)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--rc-accent-pink)]"
          >
            Get started free
          </button>
        </div>
        {/* Reserve tier */}
        <div className="bg-[var(--rc-ink-primary)] p-10 sm:p-12 text-[var(--rc-surface-primary)]">
          <div className="inline-block font-[family-name:var(--rc-font-mono)] text-[9px] tracking-[0.15em] uppercase bg-[var(--rc-accent-acid)] text-[var(--rc-ink-primary)] px-2.5 py-1 rounded-[var(--rc-button-radius)] mb-6 font-bold">Beta offer</div>
          <div className="font-[family-name:var(--rc-font-display)] text-[36px] sm:text-[40px] tracking-[0.05em] leading-none mb-2 font-bold">Reserve</div>
          <div className="font-[family-name:var(--rc-font-display)] text-[64px] sm:text-[72px] leading-none tracking-[0.02em] text-[var(--rc-accent-acid)] mb-1 font-bold">$5.99</div>
          <div className="font-[family-name:var(--rc-font-mono)] text-[11px] tracking-[0.1em] uppercase text-white/40 mb-8">Per month after trial</div>
          <ul className="flex flex-col gap-3 mb-10 list-none">
            {[
              'Unlimited bottles',
              'Unlimited Remy conversations + voice',
              'Bulk import (CSV, wine lists)',
              'Pulse dashboard + cellar intelligence',
              'Export your collection',
              'Priority support',
            ].map(f => (
              <li key={f} className="flex items-start gap-2.5 text-[15px] text-white/70 leading-snug font-[family-name:var(--rc-font-body)]">
                <span className="font-[family-name:var(--rc-font-mono)] text-[11px] text-[var(--rc-accent-pink)] shrink-0 mt-0.5">—</span>{f}
              </li>
            ))}
          </ul>
          <button
            onClick={() => navigate({ to: '/login' })}
            className="font-[family-name:var(--rc-font-mono)] text-[11px] tracking-[0.1em] uppercase px-7 py-3.5 rounded-[var(--rc-button-radius)] cursor-pointer bg-[var(--rc-accent-pink)] text-[var(--rc-ink-on-accent)] border-none hover:bg-[var(--rc-accent-pink-pressed)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--rc-accent-acid)]"
          >
            2 months free with BETALAUNCH
          </button>
        </div>
      </div>
    </section>
  );
}

/* ── Beta CTA ── */
function BetaCTA() {
  const navigate = useNavigate();
  return (
    <section className="py-20 sm:py-24 px-6 sm:px-10 flex flex-col items-center text-center relative z-[1]">
      <div className="font-[family-name:var(--rc-font-mono)] text-[10px] tracking-[0.2em] uppercase text-[var(--rc-accent-pink)] mb-6">Limited beta access</div>
      <h2 className="font-[family-name:var(--rc-font-display)] text-[clamp(40px,8vw,80px)] leading-[0.95] tracking-[0.02em] mb-6 max-w-[800px] font-black">Ready to open something good?</h2>
      <p className="text-lg text-[var(--rc-ink-tertiary)] max-w-[500px] mb-4 leading-relaxed font-[family-name:var(--rc-font-body)]">
        Join the beta and get two months of Reserve free. No credit card. No obligation. Just Remy, your cellar, and a bit of civilisation.
      </p>
      <div className="flex flex-wrap items-center gap-4 my-8">
        <span className="font-[family-name:var(--rc-font-mono)] text-[10px] tracking-[0.15em] uppercase text-[var(--rc-ink-tertiary)]">Promo code</span>
        <div className="font-[family-name:var(--rc-font-mono)] text-[16px] tracking-[0.2em] font-bold text-[var(--rc-ink-primary)] bg-[var(--rc-surface-secondary)] border border-[var(--rc-border-subtle)] px-5 py-2.5 rounded-[var(--rc-button-radius)]">
          <span className="text-[var(--rc-accent-pink)]">BETALAUNCH</span>
        </div>
      </div>
      <button
        onClick={() => navigate({ to: '/login' })}
        className="font-[family-name:var(--rc-font-mono)] text-[12px] tracking-[0.1em] uppercase text-[var(--rc-ink-primary)] bg-[var(--rc-accent-acid)] px-8 py-4 rounded-[var(--rc-button-radius)] cursor-pointer border-none hover:-translate-y-0.5 transition-transform focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--rc-accent-acid)]"
      >
        Join the beta
      </button>
      <p className="font-[family-name:var(--rc-font-mono)] text-[10px] tracking-[0.08em] uppercase text-[var(--rc-ink-tertiary)] mt-5">
        2 months free · Cancels automatically if no card added · No spam ever
      </p>
    </section>
  );
}

/* ── Footer ── */
function LandingFooter() {
  return (
    <footer className="border-t border-[var(--rc-border-subtle)] px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 relative z-[1]">
      <div className="font-[family-name:var(--rc-font-display)] text-[22px] tracking-[0.08em] text-[var(--rc-ink-ghost)] font-bold">
        Rave <span className="text-[var(--rc-accent-pink)]">Cave</span>
      </div>
      <ul className="flex gap-6 list-none">
        {[{ label: 'Privacy', href: 'https://ravecave.app/privacy' }, { label: 'Terms', href: 'https://ravecave.app/terms' }].map(({ label, href }) => (
          <li key={label}>
            <button
              type="button"
              onClick={() => openExternal(href)}
              className="font-[family-name:var(--rc-font-mono)] text-[10px] tracking-[0.1em] uppercase text-[var(--rc-ink-ghost)] no-underline hover:text-[var(--rc-ink-primary)] transition-colors bg-transparent border-0 cursor-pointer p-0"
            >
              {label}
            </button>
          </li>
        ))}
      </ul>
      <div className="font-[family-name:var(--rc-font-mono)] text-[10px] text-[var(--rc-ink-ghost)] tracking-[0.05em]">&copy; 2026 Rave Cave</div>
    </footer>
  );
}

/* ── Main Landing Page ── */
export default function LandingPage() {
  const fadeRefs = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-5');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 },
    );

    fadeRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addFadeRef = (el: HTMLDivElement | null) => {
    if (el && !fadeRefs.current.includes(el)) fadeRefs.current.push(el);
  };

  return (
    <div className="fixed inset-0 overflow-y-auto overflow-x-hidden bg-[var(--rc-surface-primary)] text-[var(--rc-ink-primary)] font-[family-name:var(--rc-font-body)] text-lg leading-relaxed">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:bg-[var(--rc-accent-acid)] focus:text-[var(--rc-ink-primary)] focus:px-4 focus:py-2 focus:z-[200] font-[family-name:var(--rc-font-mono)] text-xs uppercase">
        Skip to main content
      </a>
      <LandingNav />
      <main id="main">
        <Hero />
        <div className="h-px bg-[var(--rc-border-subtle)] mx-6 sm:mx-10" />
        <Marquee />
        <div ref={addFadeRef} className="opacity-0 translate-y-5 transition-all duration-700 ease-out motion-reduce:opacity-100 motion-reduce:translate-y-0">
          <Features />
        </div>
        <div ref={addFadeRef} className="opacity-0 translate-y-5 transition-all duration-700 ease-out motion-reduce:opacity-100 motion-reduce:translate-y-0">
          <AppPreview />
        </div>
        <RemySection />
        <div ref={addFadeRef} className="opacity-0 translate-y-5 transition-all duration-700 ease-out motion-reduce:opacity-100 motion-reduce:translate-y-0">
          <SocialProof />
        </div>
        <Pricing />
        <BetaCTA />
      </main>
      <LandingFooter />
    </div>
  );
}
